import { normalizeCurve, roundedRectWithCurvePath } from "./utils/shapePaths";
import { buildMaskPath2D } from "./utils/maskPaths";

export function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const s = String(hex || "").trim();
  const m = s.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function parseRgbLike(color: string): { r: number; g: number; b: number } | null {
  const s = String(color || "").trim();
  const hex = parseHexColor(s);
  if (hex) return hex;
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
  if (!m) return null;
  const r = Math.max(0, Math.min(255, Math.round(Number(m[1] ?? 0))));
  const g = Math.max(0, Math.min(255, Math.round(Number(m[2] ?? 0))));
  const b = Math.max(0, Math.min(255, Math.round(Number(m[3] ?? 0))));
  return { r, g, b };
}

export function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const toLin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = toLin(rgb.r);
  const g = toLin(rgb.g);
  const b = toLin(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function rgbaFromRgb(rgb: { r: number; g: number; b: number }, a: number): string {
  const alpha = Math.max(0, Math.min(1, Number(a)));
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

export function colorWithAlpha(color: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, Number(alpha)));
  const rgb = parseRgbLike(color);
  if (!rgb) return color;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

export function resolveCornerRadiiPx(
  cornerRadius: any,
  width: number,
  height: number,
  element?: any
): [number, number, number, number] {
  const w = Math.max(1, Number(width) || 1);
  const h = Math.max(1, Number(height) || 1);
  const maxPx = Math.min(w, h) / 2;
  const clampPx = (v: number) => Math.max(0, Math.min(maxPx, v));

  // Check for individual corner properties on the element first
  // (e.g., cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)
  if (element) {
    const tl = Number(element.cornerTopLeft) || 0;
    const tr = Number(element.cornerTopRight) || 0;
    const bl = Number(element.cornerBottomLeft) || 0;
    const br = Number(element.cornerBottomRight) || 0;

    // If any individual corner is set, use per-corner mode
    if (tl > 0 || tr > 0 || bl > 0 || br > 0) {
      // Use uniform cornerRadius as base, then override with individual corners
      const base = typeof cornerRadius === "number" ? cornerRadius : 0;
      return [
        clampPx(tl > 0 ? tl : base),
        clampPx(tr > 0 ? tr : base),
        clampPx(br > 0 ? br : base),
        clampPx(bl > 0 ? bl : base),
      ];
    }
  }

  if (typeof cornerRadius === "number") {
    const r = Math.max(0, Math.min(maxPx, cornerRadius));
    return [r, r, r, r];
  }

  if (!cornerRadius || typeof cornerRadius !== "object") return [0, 0, 0, 0];

  const unit = cornerRadius.unit === "pct" ? "pct" : "px";
  const mode = cornerRadius.mode === "per-corner" ? "per-corner" : "uniform";
  const scale = unit === "pct" ? Math.min(w, h) : 1;
  const toPx = (v: number) => clampPx(unit === "pct" ? (Math.max(0, v) / 100) * scale : v);

  if (mode === "uniform") {
    const r = toPx(Number(cornerRadius.value) || 0);
    return [r, r, r, r];
  }

  return [
    toPx(Number(cornerRadius.tl) || 0),
    toPx(Number(cornerRadius.tr) || 0),
    toPx(Number(cornerRadius.br) || 0),
    toPx(Number(cornerRadius.bl) || 0),
  ];
}

export function konvaCornerRadiusProp(radii: [number, number, number, number]): number | number[] {
  const [tl, tr, br, bl] = radii.map((x) => (Number.isFinite(x) ? x : 0)) as [number, number, number, number];
  if (tl === tr && tr === br && br === bl) return tl;
  return [tl, tr, br, bl];
}

export function borderAlignDeltaPx(align: any, borderWidth: number): number {
  const w = Math.max(0, Number(borderWidth) || 0);
  if (align === "inside") return w / 2;
  if (align === "outside") return -w / 2;
  return 0;
}

export function clampCropRect(rect: { x: number; y: number; width: number; height: number }, boundsW: number, boundsH: number) {
  const w = Math.max(1, Number(rect.width) || 1);
  const h = Math.max(1, Number(rect.height) || 1);
  const bw = Math.max(1, Number(boundsW) || 1);
  const bh = Math.max(1, Number(boundsH) || 1);
  const minX = bw - w;
  const minY = bh - h;
  return {
    x: Math.min(0, Math.max(minX, Number(rect.x) || 0)),
    y: Math.min(0, Math.max(minY, Number(rect.y) || 0)),
    width: w,
    height: h,
  };
}

export function drawMaskPathOnCtx(
  ctx: any,
  w: number,
  h: number,
  mask: any,
  radiiPx: [number, number, number, number],
  curve: ReturnType<typeof normalizeCurve>
) {
  const type = String(mask?.type || "rect");
  const cx = w / 2;
  const cy = h / 2;
  const dx = Number.isFinite(Number(mask?.x)) ? Number(mask.x) : 0;
  const dy = Number.isFinite(Number(mask?.y)) ? Number(mask.y) : 0;
  const scale = Math.max(0.01, Number.isFinite(Number(mask?.scale)) ? Number(mask.scale) : 1);
  const rotation = Number.isFinite(Number(mask?.rotation)) ? (Number(mask.rotation) * Math.PI) / 180 : 0;

  ctx.save?.();
  ctx.translate?.(cx + dx, cy + dy);
  ctx.rotate?.(rotation);
  ctx.scale?.(scale, scale);
  ctx.translate?.(-cx, -cy);

  if (type === "rounded-rect") {
    roundedRectWithCurvePath(ctx as any, w, h, radiiPx, curve);
    ctx.restore?.();
    return;
  }

  if (type === "rect") {
    if (curve) {
      roundedRectWithCurvePath(ctx as any, w, h, [0, 0, 0, 0], curve);
      ctx.restore?.();
      return;
    }
    ctx.beginPath?.();
    ctx.rect?.(0, 0, w, h);
    ctx.closePath?.();
    ctx.restore?.();
    return;
  }

  if (type === "circle" || type === "oval") {
    const r = Math.max(0, Math.min(w, h) / 2);
    const rx = type === "circle" ? r : Math.max(0, w / 2);
    const ry = type === "circle" ? r : Math.max(0, h / 2);
    ctx.beginPath?.();
    ctx.ellipse?.(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.closePath?.();
    ctx.restore?.();
    return;
  }

  if (type === "polygon") {
    const sides = Math.max(3, Math.min(12, Math.round(Number(mask?.polygonSides ?? 6) || 6)));
    const r = Math.max(0, Math.min(w, h) / 2);
    ctx.beginPath?.();
    for (let i = 0; i < sides; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo?.(x, y);
      else ctx.lineTo?.(x, y);
    }
    ctx.closePath?.();
    ctx.restore?.();
    return;
  }

  // Fallback
  ctx.beginPath?.();
  ctx.rect?.(0, 0, w, h);
  ctx.closePath?.();
  ctx.restore?.();
}

export function maskClipFunc(params: Parameters<typeof buildMaskPath2D>[0]) {
  return (_ctx: any) => {
    const { path, clipRule } = buildMaskPath2D(params as any);
    return clipRule ? ([path, clipRule] as [Path2D, CanvasFillRule]) : ([path] as [Path2D]);
  };
}

export function getSimpleArrowPoints(direction: "right" | "left" | "up" | "down", width: number, height: number): number[] {
  switch (direction) {
    case "right":
      return [0, height / 2, width, height / 2];
    case "left":
      return [width, height / 2, 0, height / 2];
    case "up":
      return [width / 2, height, width / 2, 0];
    case "down":
      return [width / 2, 0, width / 2, height];
  }
}
